import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-pdf/dist/esm/Page/TextLayer.css'

import { Button } from 'antd'
import { Download } from 'lucide-react'
import * as React from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import styled, { keyframes } from 'styled-components'

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`

interface MultipagePdfViewerProps {
  url: string
  width?: number
  height?: number | string
  className?: string
  onLoadSuccess?: (numPages: number) => void
  onLoadError?: (error: Error) => void
  showDownload?: boolean
  filename?: string
}

const Container = styled.div<{ height?: number | string }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: ${({ height }) =>
    typeof height === 'number' ? `${height}px` : height || '70vh'};
`

const ViewerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  border-radius: 0.25rem;
  background-color: ${({ theme }) => theme.forecast.background2};
  padding: 1rem;
  gap: 1rem;

  &::-webkit-scrollbar {
    width: 0.375rem;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.forecast.background2};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.forecast.background3};
    border-radius: 0.1875rem;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.forecast.secondaryText};
  }
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 12.5rem;
  gap: 1rem;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 12.5rem;
  gap: 1rem;
  color: ${({ theme }) => theme.forecast.danger};
  text-align: center;
  padding: 1.5rem;
`

const ProgressSpinner = styled.div`
  width: 1.5rem;
  height: 1.5rem;
  border: 2px solid ${({ theme }) => theme.forecast.background3};
  border-top: 2px solid ${({ theme }) => theme.forecast.primary};
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`

export const MultipagePdfViewer: React.FC<MultipagePdfViewerProps> = ({
  url,
  width = Math.min(800, window.innerWidth * 0.6),
  height,
  className,
  onLoadSuccess,
  onLoadError,
  showDownload = false,
  filename,
}) => {
  const [numPages, setNumPages] = React.useState<number>(0)
  const [loading, setLoading] = React.useState<boolean>(true)
  const [error, setError] = React.useState<string | null>(null)

  const handleDocumentLoadSuccess = React.useCallback(
    (pdf: { numPages: number }) => {
      setNumPages(pdf.numPages)
      setLoading(false)
      setError(null)
      onLoadSuccess?.(pdf.numPages)
    },
    [onLoadSuccess]
  )

  const handleDocumentLoadError = React.useCallback(
    (error: Error) => {
      setError(error.message)
      setLoading(false)
      onLoadError?.(error)
    },
    [onLoadError]
  )

  const handleDownload = React.useCallback(() => {
    if (!url) return

    const a = document.createElement('a')
    a.href = url
    a.download = filename || 'document.pdf'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }, [url, filename])

  // Reset state when URL changes
  React.useEffect(() => {
    setLoading(true)
    setError(null)
    setNumPages(0)
  }, [url])

  return (
    <Container height={height} className={className}>
      <ViewerContainer>
        {loading && (
          <LoadingContainer>
            <ProgressSpinner />
            <span>Loading PDF...</span>
          </LoadingContainer>
        )}

        {error && (
          <ErrorContainer>
            <span>Error loading PDF: {error}</span>
            {showDownload && (
              <Button
                type='primary'
                icon={<Download size={16} />}
                onClick={handleDownload}>
                Download File Instead
              </Button>
            )}
          </ErrorContainer>
        )}

        <Document
          file={url}
          loading={
            <LoadingContainer>
              <ProgressSpinner />
              <span>Loading PDF...</span>
            </LoadingContainer>
          }
          onLoadSuccess={handleDocumentLoadSuccess}
          onLoadError={handleDocumentLoadError}>
          {Array.from(new Array(numPages), (el, index) => (
            <Page
              key={`page_${index + 1}`}
              pageNumber={index + 1}
              width={width}
              renderTextLayer={true}
              renderAnnotationLayer={true}
            />
          ))}
        </Document>
      </ViewerContainer>
    </Container>
  )
}
